(() => {
  const instances = new WeakMap();
  let observedMobileOffer = null;
  const mobileOfferObserver = window.ResizeObserver
    ? new ResizeObserver(() => syncMobileOfferHeight())
    : null;

  function syncMobileOfferHeight() {
    const bar = document.querySelector('.whiff-mobile-offer');

    if (bar !== observedMobileOffer) {
      if (mobileOfferObserver) mobileOfferObserver.disconnect();
      observedMobileOffer = bar;
      if (bar && mobileOfferObserver) mobileOfferObserver.observe(bar);
    }

    document.documentElement.style.setProperty('--whiff-offer-h', `${bar ? bar.offsetHeight : 0}px`);
  }

  function initMobileRitualCarousel(root) {
    if (!window.matchMedia("(max-width: 767px)").matches) return null;

    const scene = root.querySelector("[data-whiff-scene='ritual']");
    const track = scene && scene.querySelector("[data-ritual-worlds]");
    const panels = track ? Array.from(track.querySelectorAll("[data-ritual-panel]")) : [];
    const previousButton = scene && scene.querySelector("[data-ritual-previous]");
    const nextButton = scene && scene.querySelector("[data-ritual-next]");
    const dots = scene ? Array.from(scene.querySelectorAll("[data-ritual-dot]")) : [];
    const status = scene && scene.querySelector("[data-ritual-status]");
    if (!scene || !track || panels.length === 0) return null;

    root.classList.add("whiff-mobile-ritual-carousel");
    track.setAttribute("role", "region");
    track.setAttribute("aria-roledescription", "carousel");
    track.setAttribute("aria-label", "WHIFF ritual steps");
    track.setAttribute("tabindex", "0");

    panels.forEach((panel, index) => {
      panel.setAttribute("role", "group");
      panel.setAttribute("aria-roledescription", "slide");
      panel.setAttribute("aria-label", `${index + 1} of ${panels.length}`);
    });

    let activeIndex = 0;
    let frame = 0;

    const setActive = (index) => {
      activeIndex = Math.max(0, Math.min(panels.length - 1, index));
      scene.dataset.ritualSlide = String(activeIndex + 1);
      panels.forEach((panel, panelIndex) => {
        panel.setAttribute("aria-hidden", panelIndex === activeIndex ? "false" : "true");
      });
      dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === activeIndex;
        dot.setAttribute("aria-current", isActive ? "true" : "false");
        dot.setAttribute("tabindex", isActive ? "0" : "-1");
      });
      if (previousButton) previousButton.disabled = activeIndex === 0;
      if (nextButton) nextButton.disabled = activeIndex === panels.length - 1;
      if (status) status.textContent = `Step ${activeIndex + 1} of ${panels.length}`;
    };

    const scrollToSlide = (index) => {
      const nextIndex = Math.max(0, Math.min(panels.length - 1, index));
      track.scrollTo({ left: nextIndex * track.clientWidth, behavior: "smooth" });
    };

    const syncActiveSlide = () => {
      frame = 0;
      const slideWidth = track.clientWidth || 1;
      setActive(Math.round(track.scrollLeft / slideWidth));
    };

    const requestSync = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(syncActiveSlide);
    };

    const handleKeys = (event) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      event.preventDefault();
      const direction = event.key === "ArrowRight" ? 1 : -1;
      scrollToSlide(activeIndex + direction);
    };

    const handleResize = () => {
      track.scrollTo({ left: activeIndex * track.clientWidth, behavior: "auto" });
    };

    track.addEventListener("scroll", requestSync, { passive: true });
    track.addEventListener("keydown", handleKeys);
    const handlePrevious = () => scrollToSlide(activeIndex - 1);
    const handleNext = () => scrollToSlide(activeIndex + 1);
    const dotHandlers = dots.map((dot, index) => {
      const handler = () => scrollToSlide(index);
      dot.addEventListener("click", handler);
      return handler;
    });
    if (previousButton) previousButton.addEventListener("click", handlePrevious);
    if (nextButton) nextButton.addEventListener("click", handleNext);
    window.addEventListener("resize", handleResize, { passive: true });
    setActive(0);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      track.removeEventListener("scroll", requestSync);
      track.removeEventListener("keydown", handleKeys);
      if (previousButton) previousButton.removeEventListener("click", handlePrevious);
      if (nextButton) nextButton.removeEventListener("click", handleNext);
      dots.forEach((dot, index) => dot.removeEventListener("click", dotHandlers[index]));
      window.removeEventListener("resize", handleResize);
      track.removeAttribute("role");
      track.removeAttribute("aria-roledescription");
      track.removeAttribute("aria-label");
      track.removeAttribute("tabindex");
      panels.forEach((panel) => {
        panel.removeAttribute("role");
        panel.removeAttribute("aria-roledescription");
        panel.removeAttribute("aria-label");
        panel.removeAttribute("aria-hidden");
      });
      delete scene.dataset.ritualSlide;
      root.classList.remove("whiff-mobile-ritual-carousel");
    };
  }

  function initWhiff(root) {
    if (!root || instances.has(root)) return;

    if (window.Shopify && window.Shopify.designMode) {
      root.classList.add("whiff-editor-mode");
      instances.set(root, { destroy() {} });
      return;
    }

    if (!window.gsap || !window.ScrollTrigger) {
      window.setTimeout(() => initWhiff(root), 80);
      return;
    }

    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;
    gsap.registerPlugin(ScrollTrigger);

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      instances.set(root, { destroy() {} });
      return;
    }

    const experience = root.querySelector("[data-whiff-experience]");
    const stage = root.querySelector("[data-whiff-stage]");
    const sectionId = root.dataset.sectionId;
    if (!experience || !stage) return;
    const isMobile = window.matchMedia("(max-width: 767px)").matches;

    const q = gsap.utils.selector(root);
    const cleanups = [];
    const destroyRitualCarousel = initMobileRitualCarousel(root);
    if (destroyRitualCarousel) cleanups.push(destroyRitualCarousel);

    const context = gsap.context(() => {
      if (isMobile) {
        root.classList.add("whiff-mobile-flow");

        const breathScene = root.querySelector("[data-whiff-scene='breath']");
        const ritualScene = root.querySelector("[data-whiff-scene='ritual']");
        const productScene = root.querySelector("[data-whiff-scene='product']");

        if (breathScene) {
          gsap.timeline({
            defaults: { ease: "power2.inOut" },
            scrollTrigger: {
              id: `whiff-mobile-breath-${sectionId}`,
              trigger: breathScene,
              start: "top top",
              end: () => `+=${Math.round(window.innerHeight * 0.9)}`,
              scrub: 0.55,
              pin: true,
              pinSpacing: true,
              anticipatePin: 1,
              invalidateOnRefresh: true,
              onToggle: (self) => root.classList.toggle("whiff-mobile-breath-active", self.isActive),
            },
          })
            .fromTo(q("[data-breath-camera]"), { scale: 1.12 }, { scale: 1, duration: 1.15, ease: "none" }, 0)
            .fromTo(q("[data-breath-copy]"), { autoAlpha: 0.2, y: 32 }, { autoAlpha: 1, y: 0, duration: 0.48 }, 0.05)
            .fromTo(q("[data-breathe-word]"), { scale: 0.92 }, { scale: 1.035, duration: 0.5, ease: "sine.out" }, 0.12)
            .to(q("[data-breathe-word]"), { scaleX: 0.988, scaleY: 0.996, duration: 0.38, ease: "sine.inOut" }, 0.62)
            .fromTo(q("[data-breath-caption]"), { autoAlpha: 0, y: 10 }, { autoAlpha: 1, y: 0, duration: 0.34 }, 0.5)
            .to(q("[data-breathe-word]"), { scaleX: 1.012, scaleY: 1.004, duration: 0.28, ease: "sine.inOut" }, 0.98)
            .to({}, { duration: 0.22 });
        }

        if (ritualScene) {
          ScrollTrigger.create({
            id: `whiff-mobile-ritual-${sectionId}`,
            trigger: ritualScene,
            start: "top 58%",
            end: "bottom 42%",
            onToggle: (self) => root.classList.toggle("whiff-ritual-active", self.isActive),
          });
        }

        if (productScene) {
          gsap.timeline({
            defaults: { ease: "power2.inOut" },
            scrollTrigger: {
              id: `whiff-mobile-product-${sectionId}`,
              trigger: productScene,
              start: "top top",
              end: () => `+=${Math.round(window.innerHeight * 1.15)}`,
              scrub: 0.6,
              pin: true,
              pinSpacing: true,
              anticipatePin: 1,
              invalidateOnRefresh: true,
              onToggle: (self) => root.classList.toggle("whiff-mobile-product-active", self.isActive),
            },
          })
            .fromTo(q("[data-product-camera]"), { autoAlpha: 0.45, y: 24, scale: 0.94 }, { autoAlpha: 1, y: 0, scale: 1, duration: 0.7, ease: "none" }, 0)
            .fromTo(q("[data-product-copy]"), { autoAlpha: 0, y: 34 }, { autoAlpha: 1, y: 0, duration: 0.46 }, 0.08)
            .to(q("[data-product-copy]"), { autoAlpha: 0, y: -20, duration: 0.3 }, 0.7)
            .fromTo(q("[data-product-proof]"), { autoAlpha: 0, y: 22 }, { autoAlpha: 1, y: 0, duration: 0.42 }, 0.82)
            .from(q("[data-product-review]"), { autoAlpha: 0, y: 10, stagger: 0.08, duration: 0.3 }, 0.93)
            .to({}, { duration: 0.28 });
        }

        return;
      }

      const scrollDistance = () => Math.round(window.innerHeight * (window.innerWidth < 768 ? 5.6 : 7.2));
      const activeWidth = () => (window.innerWidth < 768 ? "84%" : "72%");
      const traceWidth = () => (window.innerWidth < 768 ? "8%" : "14%");
      const afterActive = () => (window.innerWidth < 768 ? "92%" : "86%");
      const afterTraces = () => (window.innerWidth < 768 ? "16%" : "28%");

      gsap.set(q("[data-whiff-scene]:not([data-whiff-scene='hero'])"), {
        autoAlpha: 0,
        pointerEvents: "none",
      });

      const timeline = gsap.timeline({
        defaults: { ease: "power2.inOut" },
        scrollTrigger: {
          id: `whiff-experience-${sectionId}`,
          trigger: experience,
          start: "top top",
          end: () => `+=${scrollDistance()}`,
          scrub: 0.85,
          pin: stage,
          pinSpacing: true,
          anticipatePin: 1,
          invalidateOnRefresh: true,
          refreshPriority: 1,
          onToggle: (self) => root.classList.toggle("whiff-scroll-active", self.isActive),
          onUpdate: (self) => {
            const animationTime = self.animation ? self.progress * self.animation.duration() : 0;
            const labels = self.animation ? self.animation.labels : {};
            const ritualIsActive = isMobile
              && animationTime >= (labels.ritual || Number.POSITIVE_INFINITY)
              && animationTime < (labels.stateOS || Number.NEGATIVE_INFINITY);
            root.classList.toggle("whiff-ritual-active", ritualIsActive);
          },
        },
      });

      timeline
        .addLabel("hero", 0)
        .to(q("[data-stuck-camera]"), { yPercent: 3.5, scale: 1.06, duration: 0.95, ease: "none" }, 0)
        .to(q("[data-stuck-copy]"), { y: 20, duration: 0.95 }, 0)
        .to(q("[data-unfinished-path]"), { width: (index) => (index === 0 ? "31%" : "46%"), duration: 0.52, stagger: 0.08 }, 0.12)
        .to(q("[data-document-cursor]"), { opacity: 0.12, duration: 0.16, repeat: 5, yoyo: true }, 0.05)

        .addLabel("reframe", 1.02)
        .set(q("[data-whiff-scene='reframe']"), { autoAlpha: 1, pointerEvents: "auto", clipPath: "inset(100% 0 0 0)" }, "reframe")
        .to(q("[data-whiff-scene='reframe']"), { clipPath: "inset(0% 0 0 0)", duration: 0.55 }, "reframe")
        .to(q("[data-whiff-scene='hero']"), { autoAlpha: 0, yPercent: -3, duration: 0.5 }, "reframe")
        .set(q("[data-whiff-scene='hero']"), { pointerEvents: "none" }, "reframe+=0.55")
        .from(q("[data-reframe-kicker]"), { autoAlpha: 0, y: 12, duration: 0.28 }, "reframe+=0.18")
        .from(q("[data-distance-task]"), { autoAlpha: 0, x: -14, duration: 0.32 }, "reframe+=0.24")
        .from(q("[data-distance-question]"), { autoAlpha: 0, y: 20, duration: 0.46 }, "reframe+=0.3")
        .to(q("[data-reframe-camera]"), { scale: 1.055, xPercent: -1.5, duration: 0.94, ease: "none" }, "reframe+=0.06")

        .addLabel("breath", 2.05)
        .set(q("[data-whiff-scene='breath']"), { autoAlpha: 1, pointerEvents: "auto", scale: 1.035 }, "breath")
        .to(q("[data-whiff-scene='breath']"), { scale: 1, duration: 0.72 }, "breath")
        .to(q("[data-whiff-scene='reframe']"), { autoAlpha: 0, scale: 1.08, duration: 0.58 }, "breath")
        .set(q("[data-whiff-scene='reframe']"), { pointerEvents: "none" }, "breath+=0.72")
        .fromTo(q("[data-breath-copy]"), { autoAlpha: 0, y: 24 }, { autoAlpha: 1, y: 0, duration: 0.42 }, "breath+=0.14")
        .fromTo(q("[data-breathe-word]"), { scale: 0.94 }, { scale: 1, duration: 0.46, ease: "sine.out" }, "breath+=0.16")
        .to(q("[data-breathe-word]"), { scaleX: 1.035, scaleY: 1.012, duration: 0.46, ease: "sine.inOut" }, "breath+=0.38")
        .to(q("[data-breathe-word]"), { scaleX: 0.988, scaleY: 0.996, duration: 0.42, ease: "sine.inOut" }, "breath+=0.84")
        .fromTo(q("[data-breath-caption]"), { autoAlpha: 0, y: 8 }, { autoAlpha: 1, y: 0, duration: 0.34 }, "breath+=0.8")
        .to(q("[data-breathe-word]"), { scaleX: 1.018, scaleY: 1.006, duration: 0.34, ease: "sine.inOut" }, "breath+=1.1")
        .to(q("[data-breath-camera]"), { scale: () => (window.innerWidth < 768 ? 1.1 : 1.075), transformOrigin: "65% 34%", duration: 1.26, ease: "none" }, "breath+=0.08")

        .addLabel("ritual", 3.38)
        .set(q("[data-whiff-scene='ritual']"), { autoAlpha: 1, pointerEvents: "auto", clipPath: "inset(0 0 100% 0)" }, "ritual")
        .to(q("[data-whiff-scene='ritual']"), { clipPath: "inset(0 0 0% 0)", duration: 0.68 }, "ritual")
        .to(q("[data-whiff-scene='breath']"), { autoAlpha: 0, yPercent: -4, duration: 0.58 }, "ritual")
        .set(q("[data-whiff-scene='breath']"), { pointerEvents: "none" }, "ritual+=0.68");

      if (isMobile) {
        timeline
          .set(q("[data-ritual-worlds]"), { clipPath: "inset(0 0 0 0)" }, "ritual")
          .set(q("[data-ritual-copy='1']"), { autoAlpha: 1, y: 0 }, "ritual");
      } else {
        timeline
        .set(q("[data-ritual-panel='1']"), { left: "0%", width: activeWidth }, "ritual")
        .set(q("[data-ritual-panel='2']"), { left: activeWidth, width: traceWidth }, "ritual")
        .set(q("[data-ritual-panel='3']"), { left: afterActive, width: traceWidth }, "ritual")
        .set(q("[data-ritual-panel-shade='1']"), { opacity: 0.08 }, "ritual")
        .set(q("[data-ritual-panel-shade='2'], [data-ritual-panel-shade='3']"), { opacity: 0.62 }, "ritual")
        .fromTo(q("[data-ritual-worlds]"), { clipPath: "inset(0 100% 0 0)" }, { clipPath: "inset(0 0% 0 0)", duration: 0.68 }, "ritual")
        .fromTo(q("[data-ritual-copy='1']"), { autoAlpha: 0, y: 24 }, { autoAlpha: 1, y: 0, duration: 0.42 }, "ritual+=0.18")
        .to(q("[data-ritual-panel-image='1']"), { scale: 1.04, duration: 0.52, ease: "none" }, "ritual+=0.12")
        .to(q("[data-ritual-progress]"), { scaleX: 0.333, duration: 0.4 }, "ritual+=0.18")

        .addLabel("ritualBreathe", 4.09)
        .to(q("[data-ritual-copy='1']"), { autoAlpha: 0, y: -18, duration: 0.22 }, 4.02)
        .fromTo(q("[data-ritual-copy='2']"), { autoAlpha: 0, y: 18 }, { autoAlpha: 1, y: 0, duration: 0.34 }, 4.09)
        .to(q("[data-ritual-panel='1']"), { left: "0%", width: traceWidth, duration: 0.42 }, 4.02)
        .to(q("[data-ritual-panel='2']"), { left: traceWidth, width: activeWidth, duration: 0.42 }, 4.02)
        .to(q("[data-ritual-panel='3']"), { left: afterActive, width: traceWidth, duration: 0.42 }, 4.02)
        .to(q("[data-ritual-panel-image='1']"), { scale: 1, duration: 0.42, ease: "none" }, 4.02)
        .to(q("[data-ritual-panel-image='2']"), { scale: 1.04, duration: 0.42, ease: "none" }, 4.02)
        .to(q("[data-ritual-panel-shade='1'], [data-ritual-panel-shade='3']"), { opacity: 0.62, duration: 0.28 }, 4.02)
        .to(q("[data-ritual-panel-shade='2']"), { opacity: 0.08, duration: 0.28 }, 4.02)
        .to(q("[data-ritual-progress]"), { scaleX: 0.666, duration: 0.34 }, 4.05)

        .addLabel("ritualMove", 4.59)
        .to(q("[data-ritual-copy='2']"), { autoAlpha: 0, y: -18, duration: 0.22 }, 4.52)
        .fromTo(q("[data-ritual-copy='3']"), { autoAlpha: 0, y: 18 }, { autoAlpha: 1, y: 0, duration: 0.34 }, 4.59)
        .to(q("[data-ritual-panel='1']"), { left: "0%", width: traceWidth, duration: 0.42 }, 4.52)
        .to(q("[data-ritual-panel='2']"), { left: traceWidth, width: traceWidth, duration: 0.42 }, 4.52)
        .to(q("[data-ritual-panel='3']"), { left: afterTraces, width: activeWidth, duration: 0.42 }, 4.52)
        .to(q("[data-ritual-panel-image='2']"), { scale: 1, duration: 0.42, ease: "none" }, 4.52)
        .to(q("[data-ritual-panel-image='3']"), { scale: 1.04, duration: 0.42, ease: "none" }, 4.52)
        .to(q("[data-ritual-panel-shade='1'], [data-ritual-panel-shade='2']"), { opacity: 0.62, duration: 0.28 }, 4.52)
        .to(q("[data-ritual-panel-shade='3']"), { opacity: 0.08, duration: 0.28 }, 4.52)
        .to(q("[data-ritual-progress]"), { scaleX: 1, duration: 0.34 }, 4.55);
      }

      timeline
        .addLabel("stateOS", isMobile ? 4.55 : 5.16)
        .set(q("[data-whiff-scene='state-os']"), { autoAlpha: 1, pointerEvents: "auto", clipPath: "inset(0 0 0 100%)" }, "stateOS")
        .to(q("[data-whiff-scene='state-os']"), { clipPath: "inset(0 0 0 0%)", duration: 0.68 }, "stateOS")
        .to(q("[data-whiff-scene='ritual']"), { autoAlpha: 0, xPercent: -4, duration: 0.58 }, "stateOS")
        .set(q("[data-whiff-scene='ritual']"), { pointerEvents: "none" }, "stateOS+=0.68")
        .fromTo(q("[data-os-device]"), { autoAlpha: 0, y: 24, scale: 0.95 }, { autoAlpha: 1, y: 0, scale: 1, duration: 0.5 }, "stateOS+=0.14")
        .fromTo(q("[data-os-copy]"), { autoAlpha: 0, y: 24 }, { autoAlpha: 1, y: 0, duration: 0.48 }, "stateOS+=0.2")
        .from(q("[data-os-step]"), { autoAlpha: 0, x: 18, stagger: 0.08, duration: 0.36 }, "stateOS+=0.38")
        .to(q("[data-os-device]"), { scale: 1.025, duration: 0.36, ease: "sine.inOut" }, "stateOS+=0.4")
        .to(q("[data-os-screen-in]"), { autoAlpha: 0, duration: 0.3 }, "stateOS+=0.62")
        .fromTo(q("[data-os-screen-out]"), { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.3, immediateRender: false }, "stateOS+=0.62")
        .to(q("[data-os-device]"), { scale: 1, duration: 0.36, ease: "sine.inOut" }, "stateOS+=0.7")
        .fromTo(q("[data-os-complete]"), { autoAlpha: 0, x: 8 }, { autoAlpha: 1, x: 0, duration: 0.34 }, "stateOS+=0.82")

        .addLabel("product", "stateOS+=1.04")
        .set(q("[data-whiff-scene='product']"), { autoAlpha: 1, pointerEvents: "auto", scale: 1.045 }, "product")
        .to(q("[data-whiff-scene='product']"), { scale: 1, duration: 0.72 }, "product")
        .to(q("[data-whiff-scene='state-os']"), { autoAlpha: 0, scale: 1.08, duration: 0.58 }, "product")
        .set(q("[data-whiff-scene='state-os']"), { pointerEvents: "none" }, "product+=0.72")
        .fromTo(q("[data-product-copy]"), { autoAlpha: 0, y: 32 }, { autoAlpha: 1, y: 0, duration: 0.54 }, "product+=0.24")
        .fromTo(q("[data-product-camera]"), { autoAlpha: 0, y: 28, scale: 0.92 }, { autoAlpha: 1, y: 0, scale: 1, duration: 0.82 }, "product+=0.16")
        .to(q("[data-product-copy]"), { autoAlpha: 0, y: -18, duration: 0.26 }, "product+=0.82")
        .addLabel("productProof", "product+=0.9")
        .fromTo(q("[data-product-proof]"), { autoAlpha: 0, y: 24 }, { autoAlpha: 1, y: 0, duration: 0.48 }, "product+=0.9")
        .from(q("[data-product-review]"), { autoAlpha: 0, y: 12, stagger: 0.08, duration: 0.32 }, "product+=0.98")

        .addLabel("purchase", "product+=1.55")
        .set(q("[data-whiff-scene='purchase']"), { autoAlpha: 1, pointerEvents: "auto", clipPath: "inset(100% 0 0 0)" }, "purchase")
        .to(q("[data-whiff-scene='purchase']"), { clipPath: "inset(0% 0 0 0)", duration: 0.68 }, "purchase")
        .to(q("[data-whiff-scene='product']"), { autoAlpha: 0, yPercent: -4, duration: 0.58 }, "purchase")
        .set(q("[data-whiff-scene='product']"), { pointerEvents: "none" }, "purchase+=0.68")
        .fromTo(q("[data-proof-copy]"), { autoAlpha: 0, y: 34 }, { autoAlpha: 1, y: 0, duration: 0.58 }, "purchase+=0.24")
        .fromTo(q("[data-balance-product]"), { autoAlpha: 0, y: 20 }, { autoAlpha: 1, y: 0, duration: 0.5 }, "purchase+=0.38")
        .to(q("[data-proof-camera]"), { scale: () => (window.innerWidth < 768 ? 1.12 : 1.035), transformOrigin: "center bottom", duration: 0.98, ease: "none" }, "purchase+=0.06")
        .to({}, { duration: 0.4 })
        .addLabel("exit");

      const labelsById = {
        [`whiff-top-${sectionId}`]: "hero",
        [`whiff-reframe-${sectionId}`]: "reframe",
        [`whiff-state-shift-${sectionId}`]: "breath",
        [`whiff-ritual-${sectionId}`]: "ritual",
        [`whiff-state-os-${sectionId}`]: "stateOS",
        [`whiff-product-${sectionId}`]: "product",
        [`whiff-purchase-${sectionId}`]: "purchase",
      };

      root.querySelectorAll("a[href^='#']").forEach((anchor) => {
        const handler = (event) => {
          const id = anchor.getAttribute("href").slice(1);
          const label = labelsById[id];
          if (!label || !timeline.scrollTrigger) return;

          event.preventDefault();
          event.stopPropagation();
          const trigger = timeline.scrollTrigger;
          const progress = timeline.labels[label] / timeline.duration();
          const destination = trigger.start + (trigger.end - trigger.start) * progress;
          window.scrollTo({ top: destination, behavior: "smooth" });
        };

        anchor.addEventListener("click", handler);
        cleanups.push(() => anchor.removeEventListener("click", handler));
      });
    }, root);

    const instance = {
      destroy() {
        cleanups.forEach((cleanup) => cleanup());
        root.classList.remove("whiff-scroll-active");
        root.classList.remove("whiff-ritual-active");
        root.classList.remove("whiff-mobile-flow");
        root.classList.remove("whiff-mobile-breath-active");
        root.classList.remove("whiff-mobile-product-active");
        context.revert();
      },
    };

    instances.set(root, instance);
  }

  function initProductSelectors(scope = document) {
    scope.querySelectorAll("[data-whiff-variant-select]").forEach((select) => {
      if (select.dataset.ready === "true") return;
      select.dataset.ready = "true";
      select.addEventListener("change", () => {
        const form = select.closest("form");
        const option = select.options[select.selectedIndex];
        const input = form && form.querySelector("input[name='id']");
        const button = form && form.querySelector("button[type='submit']");
        const offer = select.closest(".whiff-offer__content, .whiff-commerce__details");
        const price = offer && offer.querySelector("[data-whiff-product-price]");
        if (input) input.value = select.value;
        if (price && option.dataset.price) price.textContent = option.dataset.price;
        if (button) {
          const available = option.dataset.available === "true";
          button.disabled = !available;
          button.textContent = available ? "ADD TO CART ↗" : "SOLD OUT";
        }
      });
    });
  }

  function openThemeCartTray() {
    const tray = document.querySelector(".js-mini-cart");
    if (!tray) return;

    tray.classList.add("active");
    document.body.classList.add("overflow-hidden");
  }

  function refreshThemeCart() {
    const miniCart = window.theme && window.theme.miniCart;

    if (miniCart) {
      if (typeof miniCart.generateCart === "function") miniCart.generateCart();
      if (typeof miniCart.updateElements === "function") miniCart.updateElements();
    }

    window.setTimeout(openThemeCartTray, 0);
  }

  function showCartError(message) {
    const alertApi = window.theme && window.theme.alert;

    if (alertApi && typeof alertApi.new === "function") {
      alertApi.new("", message, 3000, "warning");
      return;
    }

    window.alert(message);
  }

  function initProductForms(scope = document) {
    scope.querySelectorAll(".whiff-product-form").forEach((form) => {
      if (form.dataset.whiffCartReady === "true") return;
      form.dataset.whiffCartReady = "true";

      form.addEventListener("submit", async (event) => {
        event.preventDefault();

        const button = form.querySelector("button[type='submit']");
        const wasDisabled = button ? button.disabled : false;

        if (button) {
          button.disabled = true;
          button.classList.add("is-loading");
          button.setAttribute("aria-busy", "true");
        }

        try {
          const response = await fetch("/cart/add.js", {
            method: "POST",
            headers: {
              Accept: "application/json",
              "X-Requested-With": "XMLHttpRequest",
            },
            body: new FormData(form),
          });
          const result = await response.json();

          if (!response.ok) {
            throw new Error(result.description || result.message || "Unable to add this item to the cart.");
          }

          refreshThemeCart();
          document.dispatchEvent(new CustomEvent("whiff:cart:add", { detail: { item: result } }));
        } catch (error) {
          showCartError(error.message || "Unable to add this item to the cart.");
        } finally {
          if (button) {
            button.disabled = wasDisabled;
            button.classList.remove("is-loading");
            button.removeAttribute("aria-busy");
          }
        }
      });
    });
  }

  function initAll(scope = document) {
    scope.querySelectorAll("[data-whiff-root]").forEach(initWhiff);
    initProductSelectors(scope);
    initProductForms(scope);
    syncMobileOfferHeight();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll());
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (event) => initAll(event.target));
  document.addEventListener("shopify:section:unload", (event) => {
    const root = event.target.querySelector("[data-whiff-root]");
    const instance = root && instances.get(root);
    if (instance) instance.destroy();
  });

  window.addEventListener("resize", syncMobileOfferHeight);
  window.addEventListener("orientationchange", syncMobileOfferHeight);
})();
